import { useState } from "react";

import { spellOut } from "@nav-games/comms";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface BuilderProps {
  onNavigate: (to: string) => void;
}

type CallType = "mayday" | "panpan";

interface Form {
  vesselName: string;
  mmsi: string;
  callSign: string;
  position: string;
  nature: string;
  pob: string;
  extra: string;
  /// For Pan-Pan only — what kind of assistance is requested
  /// (medical advice, tow, etc.). Mayday always requests
  /// "immediate assistance" so the field is hidden in that mode.
  request: string;
}

const EMPTY: Form = {
  vesselName: "",
  mmsi: "",
  callSign: "",
  position: "",
  nature: "",
  pob: "",
  extra: "",
  request: "",
};

interface CallStyle {
  bg: string;
  word: string;
  /// Triple-repeated call prefix shown in red/amber on Channel 16.
  prefix: string;
  /// What an operator says after "this is …" instead of the Mayday
  /// "We require immediate assistance".
  closing: string;
  description: string;
}

const CALL_STYLES: Record<CallType, CallStyle> = {
  mayday: {
    bg: "#c0382b",
    word: "Mayday",
    prefix: "Mayday, Mayday, Mayday.",
    closing: "We require immediate assistance.",
    description:
      "DISTRESS — grave and imminent danger to vessel or life. Reserved for situations requiring immediate assistance.",
  },
  panpan: {
    bg: "#b45309",
    word: "Pan-Pan",
    prefix: "Pan-Pan, Pan-Pan, Pan-Pan.",
    closing: "We request assistance.",
    description:
      "URGENCY — a very urgent message about the safety of a vessel or person, but NOT in grave and imminent danger. Pick this over Mayday whenever the situation has not yet become life-threatening.",
  },
};

/// Interactive Ardent Training-style script builder. Generates a
/// Mayday (distress) OR Pan-Pan (urgency) transcript depending on
/// the call-type tab. Fields are saved in component state only —
/// no persistence on purpose: the user types in their real MMSI on
/// a live call, and we don't want stale drill data leaking into a
/// real distress.
export function Builder({ onNavigate }: BuilderProps) {
  const [callType, setCallType] = useState<CallType>("mayday");
  const [form, setForm] = useState<Form>(EMPTY);
  const [helpOpen, setHelpOpen] = useState(false);
  const style = CALL_STYLES[callType];

  const update = (key: keyof Form, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const filled = (s: string, placeholder: string) =>
    s ? <strong>{s}</strong> : <em className="muted">[{placeholder}]</em>;

  const phonetic =
    form.vesselName.trim().length > 0
      ? spellOut(form.vesselName.trim()).join(" ")
      : "";

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <h2 style={{ margin: 0 }}>VHF voice call builder</h2>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "distress" : null} onClose={() => setHelpOpen(false)} />
      <p className="muted">
        Pick the call type, type your vessel details, and read the
        transcript below from Channel 16.
      </p>

      <div className="card">
        <div style={{ display: "flex", gap: 8 }}>
          {(["mayday", "panpan"] as CallType[]).map((t) => {
            const active = callType === t;
            const s = CALL_STYLES[t];
            return (
              <button
                key={t}
                onClick={() => setCallType(t)}
                style={{
                  flex: 1,
                  padding: "12px 8px",
                  fontWeight: 700,
                  fontSize: 15,
                  borderRadius: 6,
                  border: `2px solid ${s.bg}`,
                  background: active ? s.bg : "transparent",
                  color: active ? "white" : s.bg,
                  cursor: "pointer",
                }}
              >
                {s.word}
              </button>
            );
          })}
        </div>
        <p className="small" style={{ margin: "10px 0 0", color: style.bg, fontWeight: 600 }}>
          {style.description}
        </p>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Your details</h3>
        <Field
          label="Vessel name"
          value={form.vesselName}
          onChange={(v) => update("vesselName", v)}
          placeholder="e.g. Daydream"
        />
        <Field
          label="MMSI"
          value={form.mmsi}
          onChange={(v) => update("mmsi", v)}
          placeholder="9 digits"
        />
        <Field
          label="Call sign"
          value={form.callSign}
          onChange={(v) => update("callSign", v)}
          placeholder="e.g. MABC1"
        />
        <Field
          label="Position"
          value={form.position}
          onChange={(v) => update("position", v)}
          placeholder="lat/long, or bearing & distance from a landmark"
        />
        <Field
          label={callType === "mayday" ? "Nature of distress" : "Nature of urgency"}
          value={form.nature}
          onChange={(v) => update("nature", v)}
          placeholder={
            callType === "mayday"
              ? "sinking / fire / MOB / disabled / aground …"
              : "engine failure / medical / partial steering loss …"
          }
        />
        {callType === "panpan" && (
          <Field
            label="Assistance requested"
            value={form.request}
            onChange={(v) => update("request", v)}
            placeholder="medical advice / tow to harbour / standby until …"
          />
        )}
        <Field
          label="Persons on board"
          value={form.pob}
          onChange={(v) => update("pob", v)}
          placeholder="e.g. 4"
        />
        <Field
          label="Other information (optional)"
          value={form.extra}
          onChange={(v) => update("extra", v)}
          placeholder={
            callType === "mayday"
              ? "injuries, abandoning to liferaft, EPIRB activated, etc."
              : "anchored, drifting at 1 kt, no immediate danger, etc."
          }
        />
      </div>

      <div
        className="card"
        style={{
          background: style.bg,
          color: "white",
          fontFamily: "ui-serif, Georgia, serif",
          lineHeight: 1.5,
        }}
      >
        <h3 style={{ marginTop: 0, color: "white" }}>
          Voice transcript — Channel 16
        </h3>
        <p style={{ margin: "8px 0", fontWeight: 600 }}>{style.prefix}</p>
        {callType === "panpan" && (
          <p style={{ margin: "8px 0", fontWeight: 600 }}>
            Hello all stations, hello all stations, hello all stations.
          </p>
        )}
        <p style={{ margin: "8px 0" }}>
          This is {filled(form.vesselName, "vessel name")},{" "}
          {filled(form.vesselName, "vessel name")},{" "}
          {filled(form.vesselName, "vessel name")}.
        </p>
        <p style={{ margin: "8px 0" }}>
          MMSI {filled(form.mmsi, "MMSI")}.
        </p>
        <p style={{ margin: "8px 0" }}>
          Call sign {filled(form.callSign, "call sign")}.
        </p>
        <p style={{ margin: "8px 0", fontWeight: 600 }}>
          {style.word} {filled(form.vesselName, "vessel name")}.
        </p>
        <p style={{ margin: "8px 0" }}>
          MMSI {filled(form.mmsi, "MMSI")}.
        </p>
        <p style={{ margin: "8px 0" }}>
          Call sign {filled(form.callSign, "call sign")}.
        </p>
        <p style={{ margin: "8px 0" }}>
          My position is {filled(form.position, "position")}.
        </p>
        <p style={{ margin: "8px 0" }}>
          {callType === "mayday" ? "We " : "We have "}
          {filled(
            form.nature,
            callType === "mayday" ? "nature of distress" : "nature of urgency",
          )}
          .
        </p>
        {callType === "mayday" ? (
          <p style={{ margin: "8px 0" }}>We require immediate assistance.</p>
        ) : (
          <p style={{ margin: "8px 0" }}>
            We request{" "}
            {filled(form.request, "assistance requested")}.
          </p>
        )}
        <p style={{ margin: "8px 0" }}>
          We have {filled(form.pob, "POB")} persons on board.
        </p>
        {form.extra && (
          <p style={{ margin: "8px 0" }}>{form.extra}.</p>
        )}
        <p style={{ margin: "8px 0", fontWeight: 600 }}>Over.</p>
      </div>

      {phonetic && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Vessel name in NATO phonetic</h3>
          <p style={{ fontSize: 18, margin: "8px 0", letterSpacing: "0.05em" }}>
            {phonetic}
          </p>
          <p className="small muted" style={{ margin: 0 }}>
            Use this if the listener asks you to spell out the name.
          </p>
        </div>
      )}

      <div className="row" style={{ gap: 8, alignItems: "stretch" }}>
        <button className="secondary" onClick={() => setForm(EMPTY)}>
          Clear
        </button>
        <button
          className="secondary"
          onClick={() => onNavigate("/play/mayday")}
        >
          ← Back
        </button>
      </div>
    </>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%",
          padding: "8px 10px",
          fontSize: 14,
          borderRadius: 6,
          border: "1px solid var(--border, #d4dae2)",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}
